import { Eye, EyeOff } from 'lucide-react'
import { useId, useState, type ComponentPropsWithRef, type ReactNode } from 'react'
import { cn } from '../../lib/cn'
import s from './Input.module.css'

/**
 * Input — champ de saisie du prototype (`.field .val`) : texte, mot de passe (bascule de
 * visibilité), date et heure.
 *
 * Pas de primitive ici (corollaire de la règle 3, comme `Button`) : un `<input>` natif
 * porte déjà rôle, focus et clavier, et l'`Input` de Base UI n'émet ses `data-*`
 * qu'enveloppé dans un `Field.Root` qu'on n'utilise pas. Le seul comportement que le
 * navigateur ne fournit pas — révéler le mot de passe — est un `<button>` natif.
 *
 * Le conteneur porte la bordure — c'est lui que `className` cible, les autres props
 * vont à l'`<input>`. L'anneau de focus clavier reste celui de `base.css`, porté par
 * l'`input` lui-même (cf. le module CSS).
 *
 * ⚠️ Agnostique de la densité : ses dimensions viennent de `--control-*`.
 */

export interface InputProps extends Omit<ComponentPropsWithRef<'input'>, 'type'> {
  /**
   * `password` ajoute la bascule de visibilité ; `date` et `time` laissent le **navigateur**
   * porter la saisie.
   *
   * ⚠️ **Les deux derniers sont un corollaire de la règle 3, pas une ouverture générale.** Le
   * sélecteur de date et l'horloge du navigateur sont natifs, accessibles, localisés et tactiles
   * — les réécrire en composant serait exactement ce que le design system s'interdit. Le type
   * reste une **liste fermée** : un `type` libre laisserait entrer `number` ou `email`, dont
   * l'apparence et le comportement n'ont jamais été dessinés ici.
   */
  type?: 'text' | 'password' | 'date' | 'time'
  /** Miroir de `onChange` avec la valeur seule — la convention du design system. */
  onValueChange?: (value: string) => void
  /**
   * Posé par le code appelant (échec de validation, refus du serveur) : le composant
   * le peint (`data-invalid`) et l'annonce (`aria-invalid`), il ne juge jamais la
   * saisie lui-même — « avertir plutôt que bloquer ».
   */
  invalid?: boolean
  /** Icône de tête, en slot — comme celle de `NavItem`. */
  icon?: ReactNode
  /**
   * Des valeurs proposées, la saisie restant libre (#504). Une `<datalist>` native, le même
   * corollaire de la règle 3 que `date` et `time` : la liste du navigateur est accessible et
   * tactile, et la réécrire serait un composant de plus.
   */
  suggestions?: readonly string[]
}

export function Input({
  type = 'text',
  onValueChange,
  invalid,
  disabled,
  icon,
  className,
  onChange,
  suggestions,
  ...props
}: InputProps) {
  const [revealed, setRevealed] = useState(false)
  const listId = useId()
  const isPassword = type === 'password'
  /**
   * #258 — un champ vide s'estompe (module CSS, densité tactile) et s'éclaire au focus puis dès
   * qu'il porte une valeur. Suivi ici plutôt que par `:placeholder-shown`, qui exige un
   * placeholder et ne voit pas la valeur d'un champ contrôlé.
   */
  const [typed, setTyped] = useState(String(props.defaultValue ?? ''))
  const empty = String(props.value ?? typed) === ''

  return (
    <div
      className={cn(s.root, className)}
      data-disabled={disabled ? '' : undefined}
      data-invalid={invalid ? '' : undefined}
      data-empty={empty ? '' : undefined}
    >
      {icon !== undefined && (
        <span className={s.icon} aria-hidden>
          {icon}
        </span>
      )}
      <input
        // ⚠️ Hors mot de passe, le type demandé **traverse**. Le figer à `text` rendait un champ
        // libre là où l'on attend une date : l'opérateur y tapait ce qu'il voulait, dans le
        // format qu'il voulait, et c'est le serveur qui découvrait le problème.
        type={isPassword ? (revealed ? 'text' : 'password') : type}
        disabled={disabled}
        list={suggestions === undefined ? undefined : listId}
        aria-invalid={invalid || undefined}
        onChange={(event) => {
          setTyped(event.target.value)
          onChange?.(event)
          onValueChange?.(event.target.value)
        }}
        {...props}
      />
      {suggestions !== undefined && (
        <datalist id={listId}>
          {suggestions.map((suggestion) => (
            <option key={suggestion} value={suggestion} />
          ))}
        </datalist>
      )}
      {isPassword && (
        <button
          type="button"
          className={s.toggle}
          disabled={disabled}
          // `aria-pressed` porte l'état : le libellé reste stable, le lecteur d'écran
          // annonce « activé / désactivé ». Ne pas inverser le libellé en plus.
          aria-pressed={revealed}
          aria-label="Afficher le mot de passe"
          onClick={() => setRevealed((current) => !current)}
        >
          {/* Icône décorative : l'état est déjà annoncé par `aria-pressed`. */}
          {revealed ? <EyeOff size={17} aria-hidden /> : <Eye size={17} aria-hidden />}
        </button>
      )}
    </div>
  )
}
